import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useUserConfigState } from '@/context/UserConfigContext';
import { useOnboardingWizard } from '@/context/OnboardingWizardContext';
import { Calendar, DollarSign, User, Clock, FileText } from 'lucide-react';
import { ReviewSectionButton } from './ReviewSectionButton';
import { motion } from 'framer-motion';
import type { LEDState } from './LEDIndicator';

export const CompletionStep: React.FC = () => {
  const state = useUserConfigState();
  const { submitOnboarding, goToStep, isSubmitting } = useOnboardingWizard();
  const [error, setError] = useState<string | null>(null);
  const {
    firstName,
    lastName,
    roles,
    email,
    phone,
    paymentTermsDays,
    lateFeePercentage,
    earlyPaymentDiscountPercentage,
    earlyPaymentDays,
    autoSendReminders,
    reminderDays,
    usesShortTurn,
    rateMultipliers,
    applyHolidayPremium,
    holidayCatalogIds,
    holidayCustomIds,
    needsTravel,
  } = state;
  const useMealBreakSurcharge = state.mealConfig?.applyMealSurcharge ?? false;
  const useOvernightPremium = state.overnightConfig?.applyOvernightPremium ?? false;

  // --- Build detail summaries ---
  const profileDetails = [`${firstName} ${lastName}`, email];
  if (phone) profileDetails.push(phone);

  const hasDayRate = roles.some(r => r.billingStructure === 'day_rate');
  const hasHourly = roles.some(r => r.billingStructure === 'hourly');
  const billingLabel = hasDayRate && hasHourly ? 'Mixed' : hasDayRate ? 'Day Rate' : 'Hourly';
  const rateSheetDetails = [
    `${billingLabel} Structure`,
    `${roles.length} Rate${roles.length !== 1 ? 's' : ''} Configured`,
    ...roles.slice(0, 2).map(r => `${r.name}: $${r.rate}/${r.billingStructure === 'day_rate' ? 'day' : 'hr'}`),
  ];

  const multipliersDetails = ['Default rate multipliers set'];
  const otMultiplier = rateMultipliers.find(m => m.code === 'OT');
  if (otMultiplier) {
    multipliersDetails.push(
      `OT: ${otMultiplier.defaultMultiplier}x after ${hasDayRate ? otMultiplier.defaultPerShiftThresholdHours : otMultiplier.defaultPerWeekThresholdHours} hours`
    );
  }
  const surchargeDetails: string[] = [];
  const totalHolidays = (holidayCatalogIds?.length ?? 0) + (holidayCustomIds?.length ?? 0);
  if (applyHolidayPremium) surchargeDetails.push(`${totalHolidays} holiday${totalHolidays !== 1 ? 's' : ''} selected`);
  if (usesShortTurn) surchargeDetails.push('Short-turn enabled');
  if (useMealBreakSurcharge) surchargeDetails.push('Meal break surcharge enabled');
  if (useOvernightPremium) surchargeDetails.push('Overnight premium enabled');
  const overtimeDetails = [...multipliersDetails, ...surchargeDetails];

  const expenseCategories = state.expenseConfig?.expenseCategories ?? [];
  const activeCategories = expenseCategories.filter(c => c.method !== 'not_applicable');
  const travelExpenseDetails = [
    needsTravel ? 'Travel enabled' : 'Travel disabled',
    ...(activeCategories.length > 0
      ? [`${activeCategories.length} expense ${activeCategories.length === 1 ? 'category' : 'categories'} configured`]
      : ['No expense categories configured']),
  ];

  const invoiceDetails = [`Net-${paymentTermsDays} payment terms`];
  if (lateFeePercentage && lateFeePercentage > 0) invoiceDetails.push(`${lateFeePercentage}% late fee`);
  if (earlyPaymentDiscountPercentage && earlyPaymentDiscountPercentage > 0) {
    invoiceDetails.push(`${earlyPaymentDiscountPercentage}% discount if paid within ${earlyPaymentDays} days`);
  }
  if (autoSendReminders && reminderDays.length > 0) {
    invoiceDetails.push(`Reminders at ${reminderDays.join(', ')} days`);
  }

  // --- LED state logic ---
  const getIndicatorState = (section: string): LEDState => {
    switch (section) {
      case 'multipliers':
        return rateMultipliers.length <= 1 ? 'active' : 'complete';
      case 'travel-expenses':
        return (!needsTravel && activeCategories.length === 0) ? 'active' : 'complete';
      case 'surcharges':
        return (!usesShortTurn && !applyHolidayPremium && !useMealBreakSurcharge && !useOvernightPremium)
          ? 'active' : 'complete';
      default:
        return 'complete';
    }
  };

  const cards = [
    { icon: User, title: 'Crew Info', details: profileDetails, step: 0, section: 'profile' },
    { icon: Calendar, title: 'Rate Sheet', details: rateSheetDetails, step: 1, section: 'roles' },
    { icon: Clock, title: 'Breaks, Overtime & Surcharges', details: overtimeDetails, step: 2, section: 'multipliers' },
    { icon: DollarSign, title: 'Travel & Expenses', details: travelExpenseDetails, step: 3, section: 'travel-expenses' },
    { icon: FileText, title: 'Invoicing', details: invoiceDetails, step: 4, section: 'invoice' },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {cards.map((card, index) => (
          <motion.div
            key={card.section}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3, ease: 'easeOut' }}
          >
            <ReviewSectionButton
              icon={card.icon}
              title={card.title}
              details={card.details}
              onClick={() => goToStep(card.step)}
              ledState={getIndicatorState(card.section)}
              data-testid={`review-${card.section}-button`}
            />
          </motion.div>
        ))}
      </div>

      {error && (
        <div className="mt-4 p-4 text-sm border border-[var(--color-destructive)]/50 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] rounded">
          {error}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: cards.length * 0.05 + 0.1, duration: 0.3 }}
      >
        <Button
          className="w-full mt-6 min-h-[48px] bg-[var(--primary)] text-[var(--color-surface-0)] hover:bg-[var(--color-primary-hover)] font-heading text-base uppercase tracking-[0.08em] active:translate-y-[1px]"
          size="lg"
          onClick={async () => {
            try {
              setError(null);
              await submitOnboarding();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to complete setup. Please try again.');
            }
          }}
          disabled={isSubmitting}
          data-testid="complete-setup-button"
        >
          {isSubmitting ? (
            <span className="shimmer-submit inline-block">SETTING UP...</span>
          ) : (
            'READY TO GO \u25B8'
          )}
        </Button>
      </motion.div>

      <p className="mt-3 text-xs text-center text-muted-foreground">
        You can always update these settings later from your profile.
      </p>
    </div>
  );
};
